import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import ActionModal from '../../../Components/Modals/ActionModal/ActionModal';
import useEffectAfterFirstRender from '../../../Core/Hooks/useEffectAfterFirstRenderHook';
import RegexValidators from '../../../Core/Validation/RegexValidators';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { ChartBuilderRequest, defaultChartName } from '../ChartBuilderDefinitions';

interface IChartBuilderEditTitleModalProps {
    chartBuilderUpsertRequest: ChartBuilderRequest;
    setChartBuilderUpsertRequest: (chartBuilderRequest: ChartBuilderRequest) => void;
    saveAsModalOpen: boolean;
    setSaveAsModalOpen: (saveAsModalOpen: boolean) => void;
    actionButtonName?: string;
    handleAction: () => void;
}

const ChartBuilderEditTitleModal: React.FC<IChartBuilderEditTitleModalProps> = (props: IChartBuilderEditTitleModalProps) => {
    const [translations] = useLanguage();
    const [titleText, setTitleText] = useState<string>(props.chartBuilderUpsertRequest.name);
    const { demeterChartGuid } = useParams();

    const handleActionAndCloseModal = () => {
        props.handleAction();
        props.setSaveAsModalOpen(false);
    };

    const handleUpdateChartName = (name: string) => {
        if (titleText === props.chartBuilderUpsertRequest.name) {
            handleActionAndCloseModal();
            return;
        }

        props.setChartBuilderUpsertRequest({
            ...props.chartBuilderUpsertRequest,
            name,
        });
    };

    // Sync the name back after a chart is first saved.
    useEffect(() => {
        if (!demeterChartGuid) {
            return;
        }

        setTitleText(props.chartBuilderUpsertRequest.name);
    }, [demeterChartGuid]);

    useEffectAfterFirstRender(() => {
        if (!props.chartBuilderUpsertRequest.name || props.chartBuilderUpsertRequest.name === defaultChartName) {
            return;
        }

        handleActionAndCloseModal();
    }, [props.chartBuilderUpsertRequest.name]);

    return (
        <ActionModal
            header={translations.chartBuilder.headers.saveChart}
            showModal={props.saveAsModalOpen}
            handleCancel={() => props.setSaveAsModalOpen(false)}
            actionButtonName={props.actionButtonName ?? translations.actions.save}
            handleAction={() => handleUpdateChartName(titleText)}
            actionButtonDisabled={!titleText || titleText === defaultChartName}
            track={{ eventCategory: 'ChartBuilder', eventAction: 'ModalSaveChart' }}
        >
            <TextInput
                title={translations.words.name}
                placeholder={translations.calculators.valorization.text.enterName}
                required
                value={titleText}
                handleTextChange={(value) => setTitleText(value)}
                validation={RegexValidators.AlphaNumericMinimumLength2}
                track={{
                    eventCategory: 'ChartBuilder',
                    eventAction: 'TitleChanged',
                    eventValue: titleText,
                }}
            />
        </ActionModal>
    );
};

export default ChartBuilderEditTitleModal;
